import {
  HeaderComponent,
  BodyComponent,
  TableComponent,
  ActionComponent,
  trans,
  FilterComponent,
  ImportButton,
  DownloadButton,
  EmployeeWithProfile, SelectWithSearch, PaginationComponent, RefreshButton,
  FilterButton
} from "@comps/components";
import { makeRequest, makeRequestStateless, handleDownloadFile } from "@utils/helpers";
import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { getEmployeesReq, getFilterReq, onDownloadSampleEmployeesReq } from "./requests"
import { Commons, Const } from "../constants"
import moment from "moment";
import { Button, Drawer, Spin, message, Tooltip } from "antd";
import AddEmployee from "./components/AddEmployee";
import { getResourceDataReq, createEmploymentResource } from "@comps/commonFunctions";
import ImportEmployees from "./components/ImportEmployees";
import { CreateButton } from "@comps/components";
import { useLocation, useNavigate } from "react-router-dom";
import { clearFilterOptions } from "../../../redux/filterOptionsSlice";
import { useDispatch } from "react-redux";
import { AppstoreOutlined, FileExcelOutlined, MenuOutlined, TeamOutlined } from "@ant-design/icons";
import "./components/Employee.css";
import { downloadFullEmploymentExitReport } from "./fullEmploymentExitReportDownload";

// ==================================
//   Page Breadcrumbs Start
// ==================================

const langKey = Const["employee"].lngKey

const STATUS_SEGMENTS = [
  { value: 'active', label: 'All active', title: 'All active employees' },
  {
    value: 'inactive',
    label: 'Inactive',
    title: 'Inactive employees',
    emptyHeading: 'No inactive employees',
    emptySubline: 'Employees marked inactive will appear here.',
  },
  {
    value: 'rehired',
    label: 'Rehired',
    title: 'Rehired employees',
    emptyHeading: 'No rehired employees',
    emptySubline: 'Employees who have been rehired will appear here.',
  },
  {
    value: 'first_joiners',
    label: 'First joiners',
    title: 'First joiners',
    emptyHeading: 'No first joiners yet',
    emptySubline: 'New first-time joiners will appear here.',
  },
];

const DEFAULT_EMPTY_STATUS = {
  emptyHeading: 'No employees found',
  emptySubline: 'Employees matching this filter will appear here.',
};

// Deterministic initials + color for the employee card avatar (fallback when no profile picture)
const avatarPalette = ["#E0702B", "#3E8E6E", "#6C4BD8", "#2B7FE0", "#DB5C4C", "#C99A2E", "#2E9BA6", "#B0479B"];

const getInitials = (name = "") => {
  // Strip employee-code suffixes like "- (Ch-00006)" / "(0164)" and any non-letters
  // so names such as "ASGHAR ABBAS - (Ch-00006)" don't produce garbage like "A(".
  const parts = name
    .replace(/\(.*?\)/g, " ")
    .replace(/[-–—].*$/, " ")
    .replace(/[^A-Za-z\s]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const getAvatarColor = (name = "") => {
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return avatarPalette[sum % avatarPalette.length];
};

const getResponseStatusCounts = (response) => {
  return response?.employee_employments?.meta?.status_counts
    || response?.employee_employments?.meta?.counts
    || response?.employee_status_counts
    || response?.status_counts
    || response?.counts
    || {};
};

const EmployeeStatusEmpty = ({ segment }) => {
  const emptyState = segment || DEFAULT_EMPTY_STATUS;

  return (
    <div className="employee-status-empty">
      <div className="employee-status-empty__icon">
        <TeamOutlined />
      </div>
      <div className="employee-status-empty__heading">{ emptyState.emptyHeading || DEFAULT_EMPTY_STATUS.emptyHeading }</div>
      <div className="employee-status-empty__subline">{ emptyState.emptySubline || DEFAULT_EMPTY_STATUS.emptySubline }</div>
    </div>
  );
};

// Avatar shows the profile photo when it loads, otherwise a colored initials tile.
// Falls back to initials on broken/404 image URLs so no broken-image icon is shown.
const EmpAvatar = ({ record }) => {
  const [imgError, setImgError] = useState(false);
  const url = record.employee.profile_pic_url;
  const showPic = url && !url.includes("arnold-avatar") && !imgError;
  return (
    <div className="ec-av" style={ showPic ? { background: "transparent", boxShadow: "none" } : { background: getAvatarColor(record.employee_name) } }>
      { showPic
        ? <img src={ url } alt={ record.employee_name } onError={ () => setImgError(true) } />
        : getInitials(record.employee_name) }
    </div>
  );
};

// ==================================
//   Page Breadcrumbs End
// ==================================

const IndexEmployees = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerVisible, setDrawerVisible] = useState(false); // State to manage drawer visibility
  const [isCardView, setIsCardView] = useState(false); // false renders the employee card view.
  const [viewEmployeeData, setViewEmployeeData] = useState(null);



  // ==================================
  //   Use States Start
  // ==================================
  const [dataSource, setDataSource] = useState([]);
  const [employments, setEmployments] = useState([])
  const [defaultEmployments, setDefaultEmployments] = useState([])
  const [loader, setLoader] = useState(false);
  const [filters, setFilters] = useState('');
  const [childComponent, setChildComponent] = useState(null);
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({
    "total": 0,
    "count": 0,
    "per_page": 0,
    "current_page": 0,
    "total_pages": 0,
    "links": {}
  });

  const [employeeStatus, setEmployeeStatus] = useState('active');
  const [statusTotals, setStatusTotals] = useState({
    active: 0,
    inactive: 0,
    rehired: 0,
    first_joiners: 0,
  });
  const statusTrackRef = useRef(null);
  const statusSegmentRefs = useRef({});
  const [statusIndicator, setStatusIndicator] = useState({ left: 3, width: 0, ready: false });
  const [showActive, setShowActive] = useState(true)
  const dispatch = useDispatch();
  //=========================================

  // ==================================
  //   Use States End
  // ==================================

  // ==================================
  //   Table Data End
  // ==================================

  const columns = [
    {
      key: trans(Commons.name),
      title: trans(Commons.name),
      render: (record) => (
        <EmployeeWithProfile record={ record } />
      )
    },
    {
      key: trans(Commons.branch),
      title: trans(Commons.branch),
      dataIndex: 'branch_name',

    },
    {
      key: trans(Commons.department),
      title: trans(Commons.department),
      dataIndex: 'department_name',

    },
    {
      key: trans(Commons.designation),
      title: trans(Commons.designation),
      dataIndex: 'designation_name',

    },
    {
      key: trans(Commons.joiningDate),
      title: trans(Commons.joiningDate),
      render: (record) => {
        return moment(record.joining_date).format('DD-MMM-YYYY');
      },

    },

    {
      key: trans(Commons.grade),
      title: trans(Commons.grade),
      dataIndex: 'grade_name'
    },
    // {
    //   key: trans(Commons.ProfileCompletion),
    //   title: trans(Commons.ProfileCompletion),
    //   render: (record) => {
    //     return <Badge
    //       count={ record.employee.profile_score + '% Completed' }
    //       style={ {
    //         background: 'white', color: colors[record.employee.profile_score_class], fontSize: 12,
    //         fontWeight: 700
    //       } }
    //     />
    //   }
    // },
    {
      key: trans(Commons.actions),
      title: trans(Commons.actions), fixed: 'right',

      render: (record) => <ActionComponent modkey={ props.modkey } each={ record } onView={ onView } >
        <Tooltip title="Full Employment & Exit Report">
          <Button
            className="da-px-10 da-my-0 base-table-action-btn base-table-action-btn--view"
            type="link"
            size="middle"
            onClick={ () => onDownloadFullEmploymentExitReport(record) }
          >
            <FileExcelOutlined className="icon-style text-success" />
          </Button>
        </Tooltip>
      </ActionComponent>
    },
  ];


  // ==================================
  //   Table Data End
  // ==================================

  useEffect(() => {
    getAllEmployees();
    // eslint-disable-next-line
  }, [location.pathname, currentPage, filters, employeeStatus]);

  useEffect(() => {
    dispatch(clearFilterOptions())
    setFilters(props.filters)
  }, [employeeStatus])

  const updateStatusIndicator = useCallback(() => {
    const track = statusTrackRef.current;
    const activeSegment = statusSegmentRefs.current[employeeStatus];

    if (!track || !activeSegment) return;

    const trackRect = track.getBoundingClientRect();
    const segmentRect = activeSegment.getBoundingClientRect();
    setStatusIndicator({
      left: segmentRect.left - trackRect.left,
      width: segmentRect.width,
      ready: true,
    });
  }, [employeeStatus]);

  useLayoutEffect(() => {
    updateStatusIndicator();
  }, [employeeStatus, statusTotals, updateStatusIndicator]);

  useEffect(() => {
    updateStatusIndicator();

    const handleResize = () => updateStatusIndicator();
    window.addEventListener('resize', handleResize);

    let observer = null;
    if (typeof ResizeObserver !== 'undefined' && statusTrackRef.current) {
      observer = new ResizeObserver(handleResize);
      observer.observe(statusTrackRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      observer?.disconnect();
    };
  }, [updateStatusIndicator]);

  const getAllEmployees = () => {
    let payload = {
      page: currentPage,
      filters: `${props.filters || ''};${filters || ''};employee_status_|${employeeStatus}`,
      showActive: showActive
    };
    makeRequest(setLoader, getEmployeesReq, payload, onSuccess, onSuccess);
  }

  const onSuccess = (response) => {
    const responsePagination = response.employee_employments.meta.pagination;
    setPagination(responsePagination);
    setDataSource(response.employee_employments.data);
    setStatusTotals((current) => ({
      ...current,
      ...getResponseStatusCounts(response),
      [employeeStatus]: responsePagination.total || 0,
    }));
    if (defaultEmployments.length === 0) {
      setDefaultEmployments(response.employee_employments.data)
    }
  }

  //search employee
  const getEmployments = (id) => {
    let payload = {
      page: 1,
      type: 'employment',
      filters: `search_|${id};${props.filters};`
    }
    makeRequestStateless(getResourceDataReq, payload, onSuccessResourse, null);
  };

  const onSuccessResourse = (response) => {
    setEmployments(response.resources.data);
  }
  // Create component modal
  const onAddEmployee = () => {
    setChildComponent(<AddEmployee onCompleted={ onCompleted } />)
  }

  const onCompleted = (data) => {
    if (data) {
      getAllEmployees()
    }

    setChildComponent(null)
  }
  const onView = (record) => {
    navigate(`/employees/profile/${record.id}`, { state: { from: location.pathname, showActive } });
  };

  const onDownloadFullEmploymentExitReport = (record) => {
    downloadFullEmploymentExitReport({
      employmentId: record.id,
      joiningDate: record.joining_date,
      setLoader,
    });
  };

  const onCloseDrawer = () => {
    setDrawerVisible(false);
  };

  //---------------filters-----------------------

  const onOpenFilter = () => {

    if (showActive) {
      filterFields.unshift(
        {
          text: 'Full Name',
          type: "select",
          placeholder: trans(`${langKey}.select.employee`),
          key: "employees",
          data_key: "supporting_data.employees",
          //is_resource: true
          //filters: showActive ? '' : 'trashed_type_|onlyTrashed',
          resource_type: 'employment'
        },
      )
    } else {
      filterFields.unshift(
        {
          type: "select",
          placeholder: 'Employee',
          key: "employees",
          data_key: "supporting_data.employees",
          required: false,
          resource_type: 'employment',
          filters: "trashed_type_|onlyTrashed",
          onlyTrashed: true
        },
      )
    }
    setChildComponent(<FilterComponent filters={ filterFields } modkey={ props.modkey } selected={ filters } api={ () => getFilterReq({ showActive: showActive }) } setCurrentPage={ setCurrentPage } onFilter={ setFilters } onCloseFilter={ onCloseFilter } moduleName="address"
    />
    )
  }

  const onCloseFilter = () => {
    setChildComponent(null);

  }

  //download sample
  const onDownloadSample = () => {
    makeRequestStateless(onDownloadSampleEmployeesReq, null, onSuccessDownloadSample, onSuccessDownloadSample);
  }
  const onSuccessDownloadSample = (res, err) => {

    if (res.code === undefined) {
      // Extract file extension
      const fileExtension = res.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ? 'xlsx' : 'xlsx'
      const fileName = `employees.${fileExtension}`
      handleDownloadFile(fileName, res)
    }
    else {
      message.error(res.message)
    }
  }

  const onImport = () => {
    setChildComponent(<ImportEmployees onCompleted={ onCompleted } onSuccess={ getAllEmployees } />);

  }



  const filterFields = [

    {
      type: "select",
      placeholder: trans(`${langKey}.selectlocation`),
      key: "location",
      data_key: "organization_structure_data.location",
    },
    {
      type: "select",
      placeholder: trans(`${langKey}.selectbranch`),
      key: "branch",
      data_key: "organization_structure_data.branch",
    },
    {
      type: "select",
      placeholder: trans(`${langKey}.selectdepartment`),
      key: "department",
      data_key: "organization_structure_data.department",
      filterKey: "id",
      multiple: true
    },
    {
      type: "select",
      placeholder: trans(`${langKey}.selectgrades`),
      key: "grade",
      data_key: "supporting_data.grade",
    },
    {
      type: "select",
      placeholder: trans(`${langKey}.selectdesignation`),
      key: "designation",
      data_key: "supporting_data.designation",
    },
    {
      type: "select",
      placeholder: trans(`${langKey}.selectGender`),
      key: "gender",
      data_key: "supporting_data.gender",
    },
    {
      type: "select",
      placeholder: trans(`${langKey}.islinemanager`),
      key: "is_line_manager",
      data_key: "supporting_data.is_line_manager",
    },
    {
      type: "select",
      placeholder: trans(`${langKey}.ishod`),
      key: "is_hod",
      data_key: "supporting_data.is_hod",
    },
    {
      type: "select",
      placeholder: trans(`${langKey}.isfinalauthority`),
      key: "is_final_authority",
      data_key: "supporting_data.is_final_authority",
    },
    {
      type: "select",
      placeholder: trans(`${langKey}.selectjobtitle`),
      key: "job_title",
      data_key: "supporting_data.job_titles",
    },
    {
      type: "select",
      placeholder: trans(`${langKey}.selectemployeetype`),
      key: "employee_type",
      data_key: "supporting_data.employee_types",
    },
    {
      type: "select",
      placeholder: trans(`${langKey}.selectemploymenttype`),
      key: "contract_type",
      data_key: "supporting_data.employment_types",
    },
    {
      type: "text",
      placeholder: "CNIC",
      key: "cnic",
    },

  ]
  //----end filter--------------------
  // ==================================
  //   Component JSX Start
  // ==================================

  const onChangeEmployeeStatus = (val) => {

    setEmployeeStatus(val)
    setCurrentPage(1)
    if (val === 'inactive') {
      setShowActive(false)
    }
    else {
      setShowActive(true)
    }
  }

  const activeStatusSegment = STATUS_SEGMENTS.find((status) => status.value === employeeStatus) || STATUS_SEGMENTS[0];
  const activeStatusCount = pagination.total || statusTotals[employeeStatus] || 0;
  const employeeHeading = `${activeStatusSegment.title} ${activeStatusCount}`;
  const emptyStatusSegment = employeeStatus === 'active' ? DEFAULT_EMPTY_STATUS : activeStatusSegment;
  const shouldRenderTable = isCardView || dataSource.length === 0;

  return (
    <>

      <HeaderComponent
        modkey={ props.modkey }
        headers={ { title: employeeHeading, createEntity: 'Employee' } }
      >
        <RefreshButton onClick={ getAllEmployees } />
        <DownloadButton modkey={ props.modkey } permissionAction="create" onClick={ onDownloadSample } />
        <ImportButton onClick={ onImport } />
        <CreateButton onClick={ onAddEmployee } text="Add Employee" />
        <FilterButton onClick={ onOpenFilter } />
      </HeaderComponent>
      <BodyComponent>
        <div className="employee-toolbar">
          <div className="employee-status-switcher" aria-label="Employee status" ref={ statusTrackRef }>
            <span
              className={ statusIndicator.ready ? 'employee-status-indicator is-ready' : 'employee-status-indicator' }
              style={ {
                width: statusIndicator.width,
                transform: `translateX(${statusIndicator.left}px)`,
              } }
              aria-hidden="true"
            />
            { STATUS_SEGMENTS.map((status) => {
              const count = statusTotals[status.value] || 0;
              const isActive = employeeStatus === status.value;
              const isMuted = count === 0 && !isActive;

              return (
              <button
                key={ status.value }
                type="button"
                ref={ (node) => {
                  statusSegmentRefs.current[status.value] = node;
                } }
                className={ [
                  'employee-status-segment',
                  isActive ? 'is-active' : '',
                  isMuted ? 'is-muted' : '',
                ].filter(Boolean).join(' ') }
                onClick={ () => onChangeEmployeeStatus(status.value) }
                aria-pressed={ isActive }
              >
                { status.label }
                <span className="employee-status-count">
                  { count }
                </span>
              </button>
              );
            }) }
          </div>

          <div className="employee-toolbar-actions">
            <div className="employee-search-view-row">
              <SelectWithSearch className="search-employees"
                loading={ loader }
                placeholder="Search Employee by Name or Code"
                size="large"
                options={ createEmploymentResource(defaultEmployments.concat(employments)) }
                onChange={ (id) => {
                  setFilters(`employees_|${id};${!showActive ? "trashed_type_|onlyTrashed" : ''}`)
                  setCurrentPage(1)
                } }

                onSearch={ (id) => getEmployments(id) }
              />

              <div className="table-card-view" aria-label="Employee view">
                <button
                  type="button"
                  title="List view"
                  aria-label="List view"
                  onClick={ () => setIsCardView(true) }
                  className={ isCardView ? 'employee-view-toggle is-active' : 'employee-view-toggle' }
                >
                  <MenuOutlined />
                </button>
                <button
                  type="button"
                  title="Card view"
                  aria-label="Card view"
                  onClick={ () => setIsCardView(false) }
                  className={ isCardView ? 'employee-view-toggle' : 'employee-view-toggle is-active' }
                >
                  <AppstoreOutlined />
                </button>
              </div>
            </div>


          </div>
        </div>





        { shouldRenderTable ? (
          <TableComponent
            style={ { marginTop: 20 } }
            loader={ loader }
            columns={ columns }
            dataSource={ dataSource }
            pagination={ false }
            containerClassName="employee-directory-table"
            locale={ {
              emptyText: <EmployeeStatusEmpty segment={ emptyStatusSegment } />,
            } }
          >
          </TableComponent>
        ) : (
          <div style={ {
            height: '65vh',
            overflowY: 'scroll',
            paddingRight: 10
          } }>
            <Spin spinning={ loader }>
              <div className="emp-cards">
                { dataSource.map((record) => {
                  return (
                    <div key={ record.id } className="emp-card" onClick={ () => onView(record) }>
                      <div className="ec-top">
                        <EmpAvatar record={ record } />
                        <div className="ec-topR">
                          <span className="ec-pct">{ record.employee.profile_score }% Completed</span>
                          <svg className="ec-eye" viewBox="0 0 24 24">
                            <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </div>
                      </div>

                      <div className="ec-idblock">
                        <div className="ec-name">{ record.employee_name }</div>
                        <div className="ec-desig">{ [record.designation_name, record.code].filter(Boolean).join(" · ") }</div>
                      </div>

                      <div className="ec-table">
                        <div className="ec-row"><span>Emp ID</span><b>{ record.code || "-" }</b></div>
                        <div className="ec-row"><span>Grade</span><b>{ record.grade_name || "-" }</b></div>
                        <div className="ec-row"><span>Joining</span><b>{ record.joining_date ? moment(record.joining_date).format("DD MMM YYYY") : "-" }</b></div>
                        <div className="ec-row"><span>Department</span><b>{ record.department_name || "-" }</b></div>
                        <div className="ec-row"><span>Branch</span><b>{ record.branch_name || "-" }</b></div>
                      </div>
                    </div>
                  );
                }) }
              </div>
            </Spin>
          </div>
        ) }

        { childComponent }
      </BodyComponent>
      <PaginationComponent pagination={ pagination } modkey={ props.modkey } setCurrentPage={ setCurrentPage } currentPage={ currentPage } />
      <Drawer
        // title={ (
        //   <div>
        //     <LeftButton
        //       style={ {
        //         fontSize: '16px',
        //         marginLeft: '200px',
        //         cursor: 'pointer',
        //       } }
        //       onClick={ handleLeftArrowClick }
        //     />
        //     <RightButton
        //       style={ {
        //         fontSize: '16px',
        //         marginLeft: '10px',
        //         cursor: 'pointer',
        //       } }
        //       onClick={ handleRightArrowClick }
        //     />
        //     <MessageButton
        //       style={ {
        //         fontSize: '16px',
        //         position: 'absolute',
        //         right: 20,
        //         cursor: 'pointer',
        //       } }
        //     />
        //     <StopsButton
        //       style={ {
        //         fontSize: '16px',
        //         position: 'absolute',
        //         right: 80,
        //         cursor: 'pointer',
        //       } }
        //     />
        //   </div>
        // ) }
        placement="right"
        onClose={ onCloseDrawer }
        open={ drawerVisible }
        width={ "65%" }
      >
        <div style={ { overflow: "hidden" } }>
          <div style={ { marginTop: 60 } }>
            { viewEmployeeData }
          </div>
        </div>
      </Drawer>
    </>
  )
}


// ==================================
//   Component JSX End
// ==================================
export default IndexEmployees
